// =============================================================================
// src/chrome/copilot-sidebar/collapse-controller.ts
// Recolher / reabrir a sidebar do Copiloto + sincronizacao de layout.
//
// Proveniencia: porte FIEL do IIFE inline de unified.html linhas 2955-2985
//   (isNarrow, syncLayout, setCollapsed, restore de preferencia, listener resize).
//   Mantem a chave de localStorage 'copilotCollapsed' e a classe de body
//   'copilot-collapsed-layout' usada pelos overlays do mapa para reencaixar.
//
// SRP: so o estado recolhido/aberto. O painel de contexto e o scroll-spy vivem
//   em arquivos irmaos.
// =============================================================================

import type { CopilotElementRefs } from './copilot-element-refs';

/** Chave de preferencia persistida (unified.html 2971/2980). */
const COLLAPSED_STORAGE_KEY = 'copilotCollapsed';

/** Largura abaixo da qual a sidebar vira overlay sobreposto (2956). */
const NARROW_VIEWPORT_QUERY = '(max-width: 900px)';

/**
 * Controla recolher/reabrir a sidebar. Em telas estreitas a sidebar e um overlay
 *   (is-open); em desktop ela colapsa no proprio fluxo (is-collapsed).
 */
export class CopilotCollapseController {
  constructor(private readonly refs: CopilotElementRefs) {}

  /** Liga botoes, restaura preferencia de desktop e o listener de resize. */
  init(): void {
    this.bindToggleButtons();
    this.restorePreference();
    this.syncLayout();
    window.addEventListener('resize', () => this.syncLayout());
  }

  /** Liga collapse (recolher) e reopen (reabrir) (unified.html 2974-2975). */
  private bindToggleButtons(): void {
    if (this.refs.collapse)
      this.refs.collapse.addEventListener('click', () => this.setCollapsed(true));
    if (this.refs.reopen)
      this.refs.reopen.addEventListener('click', () => this.setCollapsed(false));
  }

  /** True se a viewport esta no modo estreito (porte de isNarrow, 2956). */
  private isNarrow(): boolean {
    return window.matchMedia(NARROW_VIEWPORT_QUERY).matches;
  }

  /**
   * Aplica/remove o estado recolhido e persiste a preferencia. Em modo estreito
   *   alterna is-open (overlay); em desktop alterna is-collapsed (porte 2964-2972).
   */
  private setCollapsed(collapsed: boolean): void {
    if (this.isNarrow()) {
      this.refs.copilot.classList.toggle('is-open', !collapsed);
      this.refs.copilot.classList.remove('is-collapsed');
    } else {
      this.refs.copilot.classList.toggle('is-collapsed', collapsed);
    }
    this.persistCollapsed(collapsed);
    this.syncLayout();
  }

  /** Grava a preferencia, ignorando localStorage indisponivel (try/catch, 2971). */
  private persistCollapsed(collapsed: boolean): void {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // Provenience: localStorage pode lancar (modo privado / quota); ignorado
      //   no original — preferencia e best-effort, nunca quebra a UI.
    }
  }

  /**
   * Marca body.copilot-collapsed-layout quando a sidebar esta escondida, para os
   *   overlays do mapa voltarem a borda esquerda (porte de syncLayout, 2957-2963).
   */
  private syncLayout(): void {
    const hidden = this.isNarrow()
      ? !this.refs.copilot.classList.contains('is-open')
      : this.refs.copilot.classList.contains('is-collapsed');
    document.body.classList.toggle('copilot-collapsed-layout', hidden);
  }

  /**
   * Restaura preferencia — somente desktop. Mobile sempre inicia recolhido
   *   (porte de unified.html 2978-2983).
   */
  private restorePreference(): void {
    try {
      if (this.isNarrow()) return;
      const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      if (stored === '1') this.refs.copilot.classList.add('is-collapsed');
    } catch {
      // Provenience: idem persistCollapsed — leitura best-effort.
    }
  }
}
